import Image from "next/image";
import { cn } from "@/lib/utils";
import { type SectionPadding } from "@/lib/shared-types";
import { type HeadingSize, HEADING_SIZE_CLASSES } from "@/lib/shared-types";
import { type ColorScheme, COLOR_SCHEMES } from "@/lib/colorSchemes";

export interface MosaicStatementProps {
  /** The large bold statement text. */
  statement: string;
  /** Array of images — split in half into two opposing tracks. */
  images: { src: string; alt: string }[];
  /** Color scheme — controls background and text colours. Defaults to "navy". */
  colorScheme?: ColorScheme;
  /** Seconds for one full scroll cycle. Default: 20 */
  interval?: number;
  /** Vertical padding size. Defaults to "default" (1.6rem). */
  padding?: SectionPadding;
  /** Heading size preset. Defaults to "lg". */
  headingSize?: HeadingSize;
}

function MosaicTrack({
  images,
  direction,
}: {
  images: { src: string; alt: string }[];
  direction: "forward" | "reverse";
}) {
  return (
    <div
      className={cn(
        direction === "forward" ? "mosaic-t0" : "mosaic-t1",
        "flex shrink-0 flex-row md:flex-col md:shrink md:min-w-0",
      )}
    >
      {images.map((img, i) => (
        <div
          key={i}
          className={cn(
            "relative rounded-2xl overflow-hidden shrink-0 mx-2 my-4 md:mx-0 md:shrink",
            "size-[180px]",
            "md:size-[clamp(100px,20vw,250px)]",
          )}
        >
          <Image
            src={img.src}
            alt={img.alt}
            fill
            loading="eager"
            className="object-cover"
            sizes="(min-width: 1000px) 250px, (min-width: 768px) 25vw, 180px"
          />
        </div>
      ))}
    </div>
  );
}

const MOBILE_PADDING_CLASSES: Record<SectionPadding, string> = {
  default: "py-6 md:py-0",
  large: "py-12 md:py-0",
  xlarge: "py-24 md:py-0",
};

export function MosaicStatement({
  statement,
  images,
  colorScheme = "navy",
  interval = 20,
  padding = "default",
  headingSize = "lg",
}: MosaicStatementProps) {
  // Duration in seconds — accept legacy ms values (>100) and convert
  const dur = interval > 100 ? interval / 1000 : interval;

  // Split images into two tracks, duplicate each for seamless looping
  const half = Math.ceil(images.length / 2);
  const track0 = images.slice(0, half);
  const track1 = images.slice(half);
  const t0x2 = [...track0, ...track0];
  const t1x2 = [...track1, ...track1];

  const scheme = COLOR_SCHEMES[colorScheme];

  return (
    <div
      className={cn(
        "w-full md:overflow-hidden md:px-6",
        MOBILE_PADDING_CLASSES[padding],
        scheme.bg,
      )}
    >
      {/* scoped keyframes + responsive animation swap */}
      <style>{`
        @keyframes mosaic-scroll-h     { from { transform: translateX(0);    } to { transform: translateX(-50%); } }
        @keyframes mosaic-scroll-h-rev { from { transform: translateX(-50%); } to { transform: translateX(0);    } }
        @keyframes mosaic-scroll-v     { from { transform: translateY(0);    } to { transform: translateY(-50%); } }
        @keyframes mosaic-scroll-v-rev { from { transform: translateY(-50%); } to { transform: translateY(0);    } }

        .mosaic-t0 { animation: mosaic-scroll-h     ${dur}s linear infinite; will-change: transform; }
        .mosaic-t1 { animation: mosaic-scroll-h-rev ${dur}s linear infinite; will-change: transform; }

        @media (min-width: 768px) {
          .mosaic-t0 { animation-name: mosaic-scroll-v;     }
          .mosaic-t1 { animation-name: mosaic-scroll-v-rev; }
        }
      `}</style>
      <div className="md:max-w-content md:mx-auto">
        <div className="flex flex-col md:grid md:grid-cols-12 md:items-center md:gap-8">
          {/* Statement — on top on mobile, on right on desktop */}
          <div className="px-6 md:px-0 md:order-last md:col-span-6">
            <h2
              className={cn(
                HEADING_SIZE_CLASSES[headingSize],
                "text-center md:text-left font-semibold md:max-w-[500px]",
                scheme.heading,
              )}
            >
              {statement}
            </h2>
          </div>

          {/* ── Mosaic container ──────────────────────────────────
            Mobile : rows  → two horizontal strips stacked,
                             track0 scrolls LEFT, track1 scrolls RIGHT
            Desktop: columns → two vertical strips side-by-side,
                             track0 scrolls UP, track1 scrolls DOWN
        ─────────────────────────────────────────────────────── */}
          <div className="overflow-hidden pt-8 md:pt-0 md:h-120 md:col-span-6">
            {/* Mobile: flex-col stacks the two rows.
              Desktop: flex-row places the two columns side-by-side.
              items-start prevents align-items:stretch from clamping
              track dimensions to the container — tracks must keep their
              natural (content) width/height so translateX/Y(-50%) lands
              exactly at the duplicate boundary. */}
            <div className="flex flex-col items-start h-full md:flex-row md:w-full md:gap-6">
              {/* Track 0 — mobile: row → left | desktop: column → up */}
              <MosaicTrack images={t0x2} direction="forward" />

              {/* Track 1 — mobile: row → right | desktop: column → down */}
              <MosaicTrack images={t1x2} direction="reverse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MosaicStatement;
